import {
  BookPlusIcon,
  Building2Icon,
  FilePlusIcon,
  PlugIcon,
  PlusIcon,
  RepeatIcon,
  UploadIcon,
  UserPlusIcon,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactFlowProvider } from "reactflow";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";

import {
  openConnectStripeFlow,
  openUploadToFinancialAccountFlow,
} from "@/app/app/_/commands/utils";
import { ActivePanelNodeProvider } from "@/app/app/_/layouts/ActivePanelNodeContext";
import { useAppRouteState, useSyncAppRouteState } from "@/app/app/_/routing";
import { DataViewModeToggle } from "@/components/organisms/DataView/DataViewModeToggle";
import { ExternalGrantsBanner } from "@/components/organisms/ExternalGrants/ExternalGrantsBanner";
import { UtilityBarExport } from "@/components/organisms/UtilityBar/UtilityBarExport";
import { ThreadPanelLayout } from "@/app/app/_/layouts/ThreadPanelLayout";
import { NodePanelLayout } from "@/app/app/_/layouts/NodePanelLayout";
import { ThreadOnlyPanelLayout } from "@/app/app/_/layouts/ThreadOnlyPanelLayout";
import { viewSupportsNodePanel } from "@/app/app/_/layouts/nodePanelConfig";
import { FinancialAccountsSubnav } from "@/app/app/_/views/FinancialAccountsView/Subnav";
import { FinancialAccountsTopbar } from "@/app/app/_/views/FinancialAccountsView/Topbar";
import { ChartOfAccountsTopbar } from "@/app/app/_/views/ChartOfAccountsView/Topbar";
import { ContactsSubnav } from "@/app/app/_/views/ContactsView/Subnav";
import { ContactsTopbar } from "@/app/app/_/views/ContactsView/Topbar";
import { OrganizationsSubnav } from "@/app/app/_/views/OrganizationsView/Subnav";
import { ReportsViewAssembleDndProvider } from "@/app/app/_/views/ReportsView/ReportsViewAssembleDndContext";
import { isReportsAppPath } from "@/app/app/_/views/ReportsView/ReportsViewReportLayoutScope";
import { ReportsViewReportLayoutProvider } from "@/app/app/_/views/ReportsView/ReportsViewReportLayoutContext";
import { ReportsViewSubnav } from "@/app/app/_/views/ReportsView/ReportsViewSubnav";
import { REPORTS_GRID_SCROLL_CONTAINER_CLASS } from "@/app/app/_/views/ReportsView/reportsGridLayout";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { DocumentsSubnav } from "@/app/app/_/views/DocumentsView/Subnav";
import { DocumentsTopbar } from "@/app/app/_/views/DocumentsView/Topbar";
import { JournalEntriesSubnav } from "@/app/app/_/views/JournalEntriesView/Subnav";
import { JournalEntriesTopbar } from "@/app/app/_/views/JournalEntriesView/Topbar";
import { TaxCodesSubnav } from "@/app/app/_/views/TaxCodesView/Subnav";
import { TransactionsSubnav } from "@/app/app/_/views/TransactionsView/Subnav";
import { TransactionsTopbar } from "@/app/app/_/views/TransactionsView/Topbar";
import {
  RulesSubnav,
  RulesActions,
  RulesLeading,
} from "@/app/app/_/views/RulesView/Subnav";
import { Values } from "@/constants";
import {
  buildSettingsSidebar,
  type SidebarRoutingItem,
} from "@/constants/navigation";
import { isConnectionView } from "@/hooks/useConnectionSetup";
import { DialogLayer } from "@/components/organisms/Dialog";
import { useDevice } from "@/hooks/useDevice";
import { useScope } from "@/hooks/useScope";
import { PendingInvitationsGate } from "@/components/organisms/Invitations/InvitationsPendingGate";
import { ApolloPreloader } from "@/layouts/AppLayout/ApolloPreloader";
import { AppBreadcrumbs } from "@/layouts/AppLayout/AppTopbar/AppBreadcrumbs";
import { CommandCenter } from "@/layouts/AppLayout/CommandCenter";
import { AppSidebar } from "@/layouts/AppLayout/AppSidebar";
import { SettingsSidebarHeader } from "@/layouts/AppLayout/AppSidebar/SettingsSidebarHeader";
import { AppSidebarTabBar } from "@/layouts/AppLayout/AppSidebar/AppSidebarTabBar";
import { BudgetViewSubnav } from "@/app/app/_/views/BudgetView/BudgetViewSubnav";
import {
  AppTopbar,
  AppViewFilterRow,
  AppViewSubnav,
  AppViewSubnavAction,
} from "@/layouts/AppLayout/AppTopbar";
import { SettingsTopbar } from "@/layouts/AppLayout/AppTopbar/SettingsTopbar";
import { ReportsViewFiltersSubnav } from "@/app/app/_/views/ReportsView/ReportsViewFiltersSubnav";
import { ReportsViewTabsSubnav } from "@/app/app/_/views/ReportsView/ReportsViewTabsSubnav";
import { getReportsTabKey } from "@/app/app/_/views/ReportsView/ReportsView.utils";
import {
  AppLayout,
  AppLayoutContent,
  AppLayoutContentPanel,
  AppLayoutFooterArea,
  AppLayoutGroup,
  AppLayoutMainPanel,
  AppLayoutOverlayPanel,
  AppLayoutPanel,
  AppLayoutPanelGroup,
  AppLayoutPanelResizeHandle,
  AppLayoutSidebar,
  AppLayoutTopbar,
  PANEL_MIN_SIZE_PX,
  syncPanelCollapsed,
} from "@/layouts/AppLayout";
import { State } from "@/state";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/components/ui/utils";
import "reactflow/dist/style.css";

const LIST_PANEL_MIN_PX = 280;

function Inner({ children }: { children: React.ReactNode }) {
  return <ReactFlowProvider>{children}</ReactFlowProvider>;
}

/** Shared report-layout state for the reports list, detail subnav, and dashboard. */
function ReportsLayoutScope({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const routeState = useAppRouteState();
  const activeAppView = State((state) => state.activeAppView);
  const reportsRouteMatch = useMatch({ path: "/app/reports", end: true });
  const needsReportLayout =
    reportsRouteMatch != null ||
    isReportsAppPath(pathname) ||
    routeState.view === "reports" ||
    activeAppView === "reports";
  if (!needsReportLayout) {
    return children;
  }
  return (
    <ReportsViewReportLayoutProvider>
      <ReportsViewAssembleDndProvider>
        {children}
      </ReportsViewAssembleDndProvider>
    </ReportsViewReportLayoutProvider>
  );
}

export default function Page() {
  const sidebarPanelRef =
    useRef<import("react-resizable-panels").PanelImperativeHandle>(null);
  const nodePanelRef =
    useRef<import("react-resizable-panels").PanelImperativeHandle>(null);
  const threadPanelRef =
    useRef<import("react-resizable-panels").PanelImperativeHandle>(null);
  const activeAppView = State((state) => state.activeAppView);
  const activeTab = State((state) => state.activeTab);
  const activeTabNodeIds = State((state) => state.activeTabNodeIds);
  const isNodePanelOpen = State((state) => state.isNodePanelOpen);
  const setIsNodePanelOpen = State((state) => state.setIsNodePanelOpen);
  const isThreadPanelOpen = State((state) => state.isThreadPanelOpen);
  const setIsThreadPanelOpen = State((state) => state.setIsThreadPanelOpen);
  const activePanelNodeId = State((state) => state.activePanelNodeId);
  const isSidebarMinimized = State((state) => state.isSidebarMinimized);
  const setIsSidebarMinimized = State((state) => state.setIsSidebarMinimized);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { entities } = useScope();
  const isSettings = pathname.startsWith("/app/settings");
  // A phone - or a window as narrow on a computer - has room for one thing at
  // a time: the sidebar is a drawer over the page, and the node panel and the
  // thread cover the page while open rather than squeezing it.
  const { isNarrow } = useDevice();

  // The public invoice page has no command stack of its own, so its "Set up
  // Stripe payments" button links here and hands the flow over. Only this one
  // flow is addressable — a general ``?command=`` route would let any link
  // open any dialog.
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("connect") !== "stripe") return;
    params.delete("connect");
    const query = params.toString();
    navigate(`${pathname}${query ? `?${query}` : ""}`, { replace: true });
    openConnectStripeFlow();
  }, [search, pathname, navigate]);

  const settingsSidebarItems = useMemo(
    () =>
      buildSettingsSidebar(
        entities
          ? entities.map((entity) => ({
              identifier: entity.identifier,
              name: entity.name,
              isUserEntity: entity.isUserEntity,
              isAccountingFirm: entity.isAccountingFirm,
            }))
          : null,
      ),
    [entities],
  );

  const settingsResolveItemProps = useCallback(
    (item: SidebarRoutingItem) => {
      if (!item.path) {
        return {};
      }

      return {
        isActive: pathname === item.path,
        onClick: () => navigate(item.path!),
      };
    },
    [navigate, pathname],
  );

  useSyncAppRouteState();
  const routeState = useAppRouteState();
  const colors = useLayoutColors();

  // Lock body scroll for app layout
  useEffect(() => {
    const orig = {
      overscrollBehaviorX: document.body.style.overscrollBehaviorX,
      overscrollBehaviorY: document.body.style.overscrollBehaviorY,
      overflow: document.body.style.overflow,
    };
    document.body.style.overscrollBehaviorX = "none";
    document.body.style.overscrollBehaviorY = "none";
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.width = "100vw";
    document.body.style.minWidth = "100vw";
    document.body.style.maxWidth = "100vw";
    document.body.style.minHeight = "100dvh";
    document.body.style.maxHeight = "100dvh";
    return () => {
      document.body.style.overscrollBehaviorX = orig.overscrollBehaviorX;
      document.body.style.overscrollBehaviorY = orig.overscrollBehaviorY;
      document.body.style.overflow = orig.overflow;
      document.body.style.height = "";
    };
  }, []);

  const leadingMap = useMemo(
    () => ({
      thread: null,
      triage: null,
      budget: null,
      reports: null,
      transactions: null,
      rules: <RulesLeading />,
      "financial-accounts": null,
      "chart-of-accounts": null,
      documents: null,
      contacts: null,
      organizations: null,
      "journal-entries": null,
      "tax-codes": null,
    }),
    [],
  );

  const navMap = useMemo(
    () => ({
      thread: null,
      triage: null,
      budget: null,
      reports: null,
      transactions: <TransactionsTopbar />,
      rules: null,
      "financial-accounts": <FinancialAccountsTopbar />,
      "chart-of-accounts": <ChartOfAccountsTopbar />,
      documents: <DocumentsTopbar />,
      contacts: <ContactsTopbar />,
      organizations: null,
      "journal-entries": <JournalEntriesTopbar />,
      "tax-codes": null,
    }),
    [],
  );

  const subnavMap = useMemo(
    () => ({
      thread: null,
      triage: null,
      budget: <BudgetViewSubnav />,
      // The list of reports, as the other lists have theirs; a report's own
      // page puts its tabs here instead (``reportsDetailSubnav``).
      reports: <ReportsViewSubnav />,
      transactions: <TransactionsSubnav />,
      rules: <RulesSubnav />,
      "financial-accounts": <FinancialAccountsSubnav />,
      "chart-of-accounts": null,
      documents: <DocumentsSubnav />,
      contacts: <ContactsSubnav />,
      organizations: <OrganizationsSubnav />,
      "journal-entries": <JournalEntriesSubnav />,
      "tax-codes": <TaxCodesSubnav />,
    }),
    [],
  );

  const actionsMap = useMemo(
    () => ({
      thread: null,
      triage: null,
      budget: null,
      reports: null,
      transactions: (
        <>
          <AppViewSubnavAction
            icon={<UploadIcon />}
            onClick={openUploadToFinancialAccountFlow}
          >
            Upload transactions
          </AppViewSubnavAction>
        </>
      ),
      rules: <RulesActions />,
      "financial-accounts": (
        <>
          <AppViewSubnavAction
            icon={<UploadIcon />}
            onClick={openUploadToFinancialAccountFlow}
          >
            Upload transactions
          </AppViewSubnavAction>
          <AppViewSubnavAction
            icon={<PlugIcon />}
            onClick={() =>
              State.getState().pushToCommandStack({
                commandDefinitionId: "connect-account-source",
              })
            }
          >
            Connect
          </AppViewSubnavAction>
          <AppViewSubnavAction
            icon={<RepeatIcon />}
            onClick={() =>
              State.getState().pushToCommandStack({
                commandDefinitionId: "sync-account",
              })
            }
          >
            Sync
          </AppViewSubnavAction>
        </>
      ),
      "chart-of-accounts": (
        <AppViewSubnavAction
          icon={<PlusIcon />}
          onClick={() =>
            State.getState().pushToCommandStack({
              commandDefinitionId: "create-ChartedAccountObject",
            })
          }
        >
          Add charted account
        </AppViewSubnavAction>
      ),
      documents: (
        <AppViewSubnavAction
          icon={<FilePlusIcon />}
          onClick={() =>
            State.getState().pushToCommandStack({
              commandDefinitionId: "create-DocumentObject",
            })
          }
        >
          Add / import documents
        </AppViewSubnavAction>
      ),
      contacts: (
        <AppViewSubnavAction
          icon={<UserPlusIcon />}
          onClick={() =>
            State.getState().pushToCommandStack({
              commandDefinitionId: "create-ContactObject",
            })
          }
        >
          Add / import contacts
        </AppViewSubnavAction>
      ),
      organizations: (
        <AppViewSubnavAction
          icon={<Building2Icon />}
          onClick={() =>
            State.getState().pushToCommandStack({
              commandDefinitionId: "create-OrganizationObject",
            })
          }
        >
          Add organization
        </AppViewSubnavAction>
      ),
      "journal-entries": (
        <AppViewSubnavAction
          icon={<BookPlusIcon />}
          onClick={() =>
            State.getState().pushToCommandStack({
              commandDefinitionId: "create-JournalEntryObject",
            })
          }
        >
          Add journal entry
        </AppViewSubnavAction>
      ),
      "tax-codes": (
        <AppViewSubnavAction
          icon={<PlusIcon />}
          onClick={() =>
            State.getState().pushToCommandStack({
              commandDefinitionId: "create-TaxObject",
            })
          }
        >
          Add tax code
        </AppViewSubnavAction>
      ),
    }),
    [],
  );

  // The dedicated ``/threads`` view has its own panel chrome — it shows
  // *either* an active node *or* the thread itself in a single right panel,
  // and the main content area renders the thread list. We render this only
  // for the thread view; every other view uses the new two-panel split
  // (NodePanelLayout below Topbar 2, or beside the full reports-detail
  // column + ThreadOnlyPanelLayout on the far right).
  const isFullWidthView = activeAppView === "thread";
  const tabKey =
    activeAppView === "reports" ? getReportsTabKey(activeTab) : activeTab;
  // Match ``ReportsLayoutScope``: use URL state so the subnav does not
  // mount without ``ReportsViewReportLayoutProvider`` while the store lags the route.
  const isReportsDetail =
    routeState.view === "reports" && Boolean(routeState.viewId);
  const isDetailView =
    routeState.view === "reports"
      ? isReportsDetail
      : Boolean((activeTabNodeIds as any)?.[activeAppView]?.[tabKey]);
  // The subnav belongs to a list — filters, sort, view mode, "add a record".
  // A routed detail page is one record, so the row has nothing to say there and
  // is dropped rather than left standing with only its view actions in it.
  // (Rules and reports keep theirs: their detail views put real chrome in it.)
  const showSubnavBar =
    activeAppView !== "thread" &&
    activeAppView !== "triage" &&
    routeState.detailIdentifier == null;
  const viewHasNodePanel =
    !isFullWidthView && viewSupportsNodePanel(activeAppView);
  // A report's own page: its filters in the row under the breadcrumbs, led by
  // the business it belongs to, and its tabs in the row under them.
  const reportsDetailSubnav = isReportsDetail ? (
    <ReportsViewFiltersSubnav />
  ) : null;
  // The list of reports has its bar in the row above, and nothing under it.
  const reportsTabsRow = isReportsDetail ? <ReportsViewTabsSubnav /> : null;
  const subnavFilters =
    reportsDetailSubnav ?? (isDetailView ? null : subnavMap[activeAppView]);
  // A list drawn as a table or as cards: which, at the row's end, past what
  // is done to the list - on a phone, where that waits behind "⋯", first in
  // the row, as one of the list's own controls.
  const modeToggle =
    !isDetailView &&
    [
      "transactions",
      "financial-accounts",
      "documents",
      "contacts",
      "organizations",
      "journal-entries",
      "tax-codes",
    ].includes(activeAppView) ? (
      <DataViewModeToggle />
    ) : null;
  const subnavLeading = isDetailView
    ? null
    : (leadingMap[activeAppView] ?? (isNarrow ? modeToggle : null));
  // A list's export, right of the view's own actions.
  const exportAction =
    subnavFilters && isConnectionView(activeAppView) ? (
      <UtilityBarExport />
    ) : null;
  const endToggle = isNarrow ? null : modeToggle;
  const subnavActions =
    actionsMap[activeAppView] || exportAction || endToggle ? (
      <>
        {actionsMap[activeAppView]}
        {exportAction}
        {endToggle}
      </>
    ) : null;
  const hasSubnavContent = Boolean(
    subnavFilters ||
    subnavLeading ||
    modeToggle ||
    actionsMap[activeAppView] ||
    navMap[activeAppView] ||
    reportsDetailSubnav ||
    reportsTabsRow,
  );

  // The contextual Node panel is *always* mounted (so ``react-resizable-panels``
  // can register its constraints once and reuse them across view changes) —
  // it just collapses to zero size whenever the active view doesn't support
  // it (see ``nodePanelConfig.ts``) or the user has closed it. When open
  // with no active id, the panel renders an empty "Select an item…" state.
  const nodePanelIsCollapsed = !viewHasNodePanel || !isNodePanelOpen;
  const threadPanelIsCollapsed = isFullWidthView
    ? !(activePanelNodeId && isNodePanelOpen)
    : !isThreadPanelOpen;

  // On a phone a page opens with nothing over it - on first load, and on
  // every page gone to after: a panel is only ever up because something was
  // just tapped to open it, never left over. The drawer shuts again once a
  // page is picked from it.
  useEffect(() => {
    if (!isNarrow) return;
    setIsNodePanelOpen(false);
    setIsThreadPanelOpen(false);
  }, [isNarrow, pathname, setIsNodePanelOpen, setIsThreadPanelOpen]);
  useEffect(() => {
    if (isNarrow) setIsSidebarMinimized(true);
  }, [isNarrow, pathname, setIsSidebarMinimized]);

  const handleNodePanelLayoutChange = useCallback(
    (layout: Record<string, number>) => {
      if (nodePanelIsCollapsed && (layout["node-panel"] ?? 0) > 0) {
        syncPanelCollapsed(
          nodePanelRef.current,
          document.getElementById("node-panel"),
          true,
        );
        return;
      }
      // Kept for the next time the page is opened: only while the panel is
      // open, so shutting it does not forget the size it was left at.
      if (!nodePanelIsCollapsed && (layout["node-panel"] ?? 0) > 0) {
        State.getState().setNodePanelSize(layout["node-panel"]);
      }
    },
    [nodePanelIsCollapsed],
  );

  const handleThreadPanelLayoutChange = useCallback(
    (layout: Record<string, number>) => {
      if (threadPanelIsCollapsed && (layout["thread-panel"] ?? 0) > 0) {
        syncPanelCollapsed(
          threadPanelRef.current,
          document.getElementById("thread-panel"),
          true,
        );
      }
    },
    [threadPanelIsCollapsed],
  );

  // Width of the main column (stable while splitting content/node/thread).
  // Pixel mins are derived from it so they always fit and never blank-screen.
  const mainColumnRef = useRef<HTMLDivElement>(null);
  const [mainColumnWidth, setMainColumnWidth] = useState(0);
  useLayoutEffect(() => {
    const el = mainColumnRef.current;
    if (!el) return;
    const update = () => setMainColumnWidth(el.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const panelMins = useMemo(() => {
    const budget = mainColumnWidth > 100 ? mainColumnWidth : 1200;
    const thread = Math.min(PANEL_MIN_SIZE_PX, Math.floor(budget * 0.28));
    const node = Math.min(PANEL_MIN_SIZE_PX, Math.floor(budget * 0.32));
    const list = Math.min(LIST_PANEL_MIN_PX, Math.floor(budget * 0.22));
    const contentWithNode = Math.min(node + list, budget - thread - 16);
    return {
      thread,
      node,
      list,
      content: !nodePanelIsCollapsed ? contentWithNode : list,
    };
  }, [mainColumnWidth, nodePanelIsCollapsed]);

  const viewSubnavRows = (
    <>
      {/* A record's page on a phone: its title, under the top bar - which has
          no room for the trail that says it on a desktop. */}
      {isNarrow && <AppBreadcrumbs />}
      {showSubnavBar && hasSubnavContent && (
        <AppLayoutTopbar borderBottom={true} height={Values.SUBNAV_HEIGHT}>
          <AppViewSubnav
            leading={subnavLeading}
            filters={subnavFilters}
            nav={navMap[activeAppView]}
            actions={subnavActions}
          />
        </AppLayoutTopbar>
      )}
      {showSubnavBar && reportsTabsRow && (
        <AppLayoutTopbar borderBottom={true} height={Values.SUBNAV_HEIGHT}>
          <AppViewFilterRow>{reportsTabsRow}</AppViewFilterRow>
        </AppLayoutTopbar>
      )}
    </>
  );

  const outletContent = (
    <AppLayoutContent
      // A report is cards raised off the page, so the canvas behind them
      // is recessed to the content surface and the cards carry the main
      // one - the reverse of every other view, which is written on
      // content with the page behind it.
      className={cn(
        isReportsDetail && [
          REPORTS_GRID_SCROLL_CONTAINER_CLASS,
          "bg-muted dark:bg-[#151719]",
        ],
      )}
    >
      <Outlet />
    </AppLayoutContent>
  );

  // The view's column: its subnav, its content, and - along the bottom of
  // that content, across however many areas the view divides it into - the
  // footer the view fills (``AppLayoutFooter``). Never under the node panel
  // or the thread.
  const viewContent = (
    // On a phone a panel is put up over the page; whatever the page stacks
    // inside itself (a filter chip's segments sit at 999) stays under it.
    <AppLayoutFooterArea className={cn(isNarrow && "isolate")}>
      {viewSubnavRows}
      {outletContent}
    </AppLayoutFooterArea>
  );

  const settingsMainContent = (
    <AppLayoutGroup orientation="vertical">
      <AppLayoutTopbar borderBottom={true}>
        <SettingsTopbar
          sidebarPanelRef={sidebarPanelRef}
          items={settingsSidebarItems}
        />
      </AppLayoutTopbar>
      <AppLayoutContent className="overflow-hidden p-0">
        <Outlet />
      </AppLayoutContent>
    </AppLayoutGroup>
  );

  const threadPanelDefaultLayout = !threadPanelIsCollapsed
    ? { content: 75, "thread-panel": 25 }
    : { content: 100, "thread-panel": 0 };

  // Read, not subscribed to: the panel group takes its layout when it is put
  // up, and a page drawn again at every step of a resize would be for nothing.
  const nodePanelSize = State.getState().nodePanelSize ?? 30;
  const nodePanelDefaultLayout = !nodePanelIsCollapsed
    ? { "inner-content": 100 - nodePanelSize, "node-panel": nodePanelSize }
    : { "inner-content": 100, "node-panel": 0 };

  const sidebar = (
    <AppSidebar
      header={isSettings ? <SettingsSidebarHeader /> : undefined}
      // On a phone the sidebar is More's sheet, and the whole of it: every
      // place, the tab bar's too, and the quick actions - the way to make
      // something there, with no Create in the top bar.
      showQuickActions={!isSettings}
      items={isSettings ? settingsSidebarItems : undefined}
      resolveItemProps={isSettings ? settingsResolveItemProps : undefined}
    />
  );

  const appMainContent = (
    <AppLayoutGroup orientation="vertical">
      <AppLayoutTopbar borderBottom={true}>
        <AppTopbar
          sidebarPanelRef={sidebarPanelRef}
          disableOpen={false}
          forceOpen={false}
        />
      </AppLayoutTopbar>

      <div
        ref={mainColumnRef}
        className="flex-1 min-h-0 min-w-0 w-full flex relative"
      >
        {isNarrow ? (
          <>
            <ReportsLayoutScope>
              {viewContent}
              {viewHasNodePanel && !nodePanelIsCollapsed && (
                <AppLayoutOverlayPanel id="node-panel">
                  <NodePanelLayout />
                </AppLayoutOverlayPanel>
              )}
            </ReportsLayoutScope>
            {!threadPanelIsCollapsed && (
              <AppLayoutOverlayPanel id="thread-panel">
                {isFullWidthView ? (
                  <ThreadPanelLayout />
                ) : (
                  <ThreadOnlyPanelLayout />
                )}
              </AppLayoutOverlayPanel>
            )}
          </>
        ) : (
          <AppLayoutPanelGroup
            direction="horizontal"
            id="app-thread-panel-group"
            defaultLayout={threadPanelDefaultLayout}
            onLayoutChange={handleThreadPanelLayoutChange}
          >
            <AppLayoutContentPanel id="content" minSize={panelMins.content}>
              {/* Reports layout/DnD providers wrap the view *and* the node panel —
                the report's widget catalogue lives in the panel and drags onto
                the canvas, so both have to sit inside the one ``DndContext`` —
                but never the global thread panel: wrapping that remounts
                ``ThreadOnlyPanelLayout`` when report layout state updates
                (e.g. ``cache-and-network`` refetches). */}
              <ReportsLayoutScope>
                {viewHasNodePanel ? (
                  <AppLayoutPanelGroup
                    direction="horizontal"
                    id="app-node-panel-group"
                    defaultLayout={nodePanelDefaultLayout}
                    onLayoutChange={handleNodePanelLayoutChange}
                  >
                    <AppLayoutContentPanel
                      id="inner-content"
                      minSize={panelMins.list}
                    >
                      {viewContent}
                    </AppLayoutContentPanel>
                    <AppLayoutPanelResizeHandle
                      disabled={nodePanelIsCollapsed}
                    />
                    <AppLayoutPanel
                      id="node-panel"
                      panelRef={nodePanelRef}
                      defaultSize={panelMins.node}
                      minSize={panelMins.node}
                      collapsible={true}
                      collapsedSize={0}
                      disabled={nodePanelIsCollapsed}
                      isMinimized={nodePanelIsCollapsed}
                      groupResizeBehavior="preserve-pixel-size"
                      onCollapse={() => setIsNodePanelOpen(false)}
                      onExpand={() => setIsNodePanelOpen(true)}
                    >
                      <NodePanelLayout />
                    </AppLayoutPanel>
                  </AppLayoutPanelGroup>
                ) : (
                  viewContent
                )}
              </ReportsLayoutScope>
            </AppLayoutContentPanel>
            <AppLayoutPanelResizeHandle disabled={threadPanelIsCollapsed} />
            <AppLayoutPanel
              id="thread-panel"
              panelRef={threadPanelRef}
              defaultSize={panelMins.thread}
              minSize={panelMins.thread}
              collapsible={true}
              collapsedSize={0}
              disabled={threadPanelIsCollapsed}
              isMinimized={threadPanelIsCollapsed}
              // The chat opens at the width kept for it - its own, or the one
              // the thread view's list implies - not the record panel's.
              openSize={
                isFullWidthView ? null : State.getState().threadPanelWidth
              }
              groupResizeBehavior="preserve-pixel-size"
              // How wide the chat is while it is open beside a page - not the
              // thread view's record panel in this slot, nor the chat on its
              // way shut - for the thread view to size its list from.
              onResize={({ inPixels }) => {
                if (
                  !isFullWidthView &&
                  !threadPanelIsCollapsed &&
                  inPixels > 0
                ) {
                  State.getState().setThreadPanelWidth(inPixels);
                }
              }}
              onCollapse={() =>
                isFullWidthView
                  ? setIsNodePanelOpen(false)
                  : setIsThreadPanelOpen(false)
              }
              onExpand={() =>
                isFullWidthView
                  ? setIsNodePanelOpen(true)
                  : setIsThreadPanelOpen(true)
              }
            >
              {isFullWidthView ? (
                <ThreadPanelLayout />
              ) : (
                <ThreadOnlyPanelLayout />
              )}
            </AppLayoutPanel>
          </AppLayoutPanelGroup>
        )}
      </div>
      {isNarrow && <AppSidebarTabBar />}
    </AppLayoutGroup>
  );

  return (
    <Inner>
      <PendingInvitationsGate>
        <ActivePanelNodeProvider>
          <AppLayout>
            <ApolloPreloader />
            {/* Every command's dialog, whatever opened it - here, not in the
                sidebar's search, which a phone and the settings pages don't
                draw. */}
            <CommandCenter />

            {/* ── Vertical: a client's books opened, across the whole
                screen | Sidebar | Main (resizable) ── */}
            <AppLayoutGroup orientation="vertical">
              <ExternalGrantsBanner />
              <AppLayoutPanelGroup
                key={isNarrow ? "narrow" : "wide"}
                direction="horizontal"
                id="app-sidebar-group"
                defaultLayout={
                  isNarrow ? { main: 100 } : { sidebar: 15, main: 85 }
                }
              >
                {isNarrow ? null : (
                  <>
                    <AppLayoutSidebar
                      panelRef={sidebarPanelRef}
                      isMinimized={isSidebarMinimized}
                      onCollapse={() => setIsSidebarMinimized(true)}
                      onExpand={() => setIsSidebarMinimized(false)}
                      style={
                        isSidebarMinimized
                          ? { position: "relative", zIndex: 2 }
                          : undefined
                      }
                    >
                      {sidebar}
                    </AppLayoutSidebar>
                    <AppLayoutPanelResizeHandle />
                  </>
                )}
                <AppLayoutMainPanel>
                  {isSettings ? settingsMainContent : appMainContent}
                </AppLayoutMainPanel>
              </AppLayoutPanelGroup>
            </AppLayoutGroup>
            {isNarrow && (
              // A sheet up from the tab bar's More, as tall as what is in it.
              // Not modal: its poppers - the business menu - are drawn in a
              // portal of their own, and take the keyboard and a press too.
              <Sheet
                modal={false}
                open={!isSidebarMinimized}
                onOpenChange={(open) => !open && setIsSidebarMinimized(true)}
              >
                {!isSidebarMinimized && (
                  <div aria-hidden className="fixed inset-0 z-50 bg-black/50" />
                )}
                <SheetContent
                  side="bottom"
                  onInteractOutside={(event) => {
                    if (
                      (event.target as Element | null)?.closest("[data-popper]")
                    ) {
                      event.preventDefault();
                    }
                  }}
                  className="h-auto max-h-[80dvh] overflow-y-auto rounded-t-lg pb-[env(safe-area-inset-bottom)]"
                  style={{ backgroundColor: colors.sidebar }}
                >
                  <SheetTitle className="sr-only">More</SheetTitle>
                  <DialogLayer>{sidebar}</DialogLayer>
                </SheetContent>
              </Sheet>
            )}
          </AppLayout>
        </ActivePanelNodeProvider>
      </PendingInvitationsGate>
    </Inner>
  );
}
